import { z } from 'zod';

/**
 * The dashboard's range presets (D12).
 *
 * A closed set rather than a free `from`/`to`: the server resolves the actual
 * window (and its bucket) from the preset in the platform's active timezone, so
 * a client cannot ask for a window whose bucketing would depend on a setting it
 * cannot see (P25b2's trap).
 */
export const STATS_RANGE_KEYS = ['7d', '30d', '90d', '12m'] as const;

export const StatsRangeKeySchema = z.enum(STATS_RANGE_KEYS);

export type StatsRangeKey = (typeof STATS_RANGE_KEYS)[number];

/** Every dashboard stats endpoint's query — a range preset, defaulted server-side. */
export const StatsQuerySchema = z
  .object({
    range: StatsRangeKeySchema.default('30d'),
  })
  .strict();

export type StatsQuery = z.infer<typeof StatsQuerySchema>;

/** The granularity a window's series are bucketed at. */
export const STATS_BUCKETS = ['day', 'month'] as const;

export const StatsBucketSchema = z.enum(STATS_BUCKETS);

export type StatsBucket = (typeof STATS_BUCKETS)[number];

/**
 * The window the server resolved, echoed back so a chart can label its own
 * axis and its "vs previous period" comparisons without re-deriving them.
 *
 * `previousFrom`/`previousTo` name the immediately preceding window of the same
 * length — what every KPI's `trend` compares against.
 */
export const StatsWindowSchema = z
  .object({
    range: StatsRangeKeySchema,
    bucket: StatsBucketSchema,
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    previousFrom: z.iso.datetime(),
    previousTo: z.iso.datetime(),
  })
  .strict();

export type StatsWindow = z.infer<typeof StatsWindowSchema>;
